import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import type {
  ClientWarningDismissal,
  ClientWarningKey
} from "@/api/types/foreignNational";

/** The dismissal hiding `warning`, if one matches its current facts. */
export const activeDismissal = (
  dismissals: ClientWarningDismissal[],
  warning: ClientWarningKey | null | undefined
): ClientWarningDismissal | null =>
  (warning &&
    dismissals.find(
      (d) => d.kind === warning.kind && d.fingerprint === warning.fingerprint
    )) ||
  null;

/**
 * Warnings dismissed for the whole firm on this client. Dismissing and
 * restoring are logged to the client's activity by the server.
 */
export const useWarningDismissals = (clientId: string) => {
  const [dismissals, setState] = useState<ClientWarningDismissal[]>([]);
  const current = useRef<ClientWarningDismissal[]>([]);
  const set = useCallback((rows: ClientWarningDismissal[]) => {
    current.current = rows;
    setState(rows);
  }, []);

  useEffect(() => {
    let live = true;
    set([]);
    if (!clientId) return undefined;
    ForeignNationalsAPI.listWarningDismissals(clientId)
      .then((rows) => {
        if (live) set(rows);
      })
      .catch(() => {
        // Warnings stay visible when the list can't load.
      });
    return () => {
      live = false;
    };
  }, [clientId, set]);

  const dismiss = useCallback(
    async (warning: ClientWarningKey, reason: string | null) => {
      const previous = current.current;
      set([
        { ...warning, reason },
        ...previous.filter((d) => d.kind !== warning.kind)
      ]);
      try {
        const saved = await ForeignNationalsAPI.dismissWarning(clientId, {
          ...warning,
          reason
        });
        set([saved, ...current.current.filter((d) => d.kind !== saved.kind)]);
      } catch {
        set(previous);
        toast.error("Couldn't dismiss the warning. Try again.");
      }
    },
    [clientId, set]
  );

  const restore = useCallback(
    async (kind: string) => {
      const previous = current.current;
      set(previous.filter((d) => d.kind !== kind));
      try {
        await ForeignNationalsAPI.restoreWarning(clientId, kind);
      } catch {
        set(previous);
        toast.error("Couldn't restore the warning. Try again.");
      }
    },
    [clientId, set]
  );

  return { dismissals, dismiss, restore };
};
